/**
 * Contraste entre los dos colores de un codigo QR.
 *
 * Un lector de QR no "ve" colores: convierte la imagen a blanco y negro con un
 * umbral y luego busca el patron. Lo unico que importa, por tanto, es cuanta
 * luminancia separa los modulos del fondo. Dos colores que en pantalla se
 * distinguen de sobra pueden caer del mismo lado del umbral en una foto
 * tomada con poca luz, y entonces el codigo simplemente no existe para el
 * telefono.
 *
 * Esto es lo que convierte la personalizacion en un riesgo real: el codigo se
 * ve precioso en el panel, se manda a imprimir mil veces, y falla en la mesa
 * del restaurante. Por eso el panel no se limita a avisar, impide guardar por
 * debajo del minimo.
 */

/** Convierte `#rrggbb` (o `#rgb`) en sus tres componentes 0-255. */
function parseHex(color: string): [number, number, number] | null {
  const hex = color.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

/** Luminancia relativa segun la formula de WCAG. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (value: number): number => {
    const s = value / 255;
    return s <= 0.039_28 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Razon de contraste entre dos colores, de 1 (identicos) a 21 (negro sobre
 * blanco). Devuelve null si alguno no es un color valido.
 */
export function contrastRatio(a: string, b: string): number | null {
  const first = parseHex(a);
  const second = parseHex(b);
  if (!first || !second) return null;

  const la = relativeLuminance(first);
  const lb = relativeLuminance(second);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Minimo para permitir guardar.
 *
 * 4.5 es el umbral de texto de WCAG. Se toma prestado porque resuelve el mismo
 * problema —distinguir dos tonos en condiciones imperfectas— y porque es un
 * numero defendible en vez de uno inventado. Por debajo de ahi, un codigo
 * impreso deja de leerse en cuanto la luz no acompana.
 */
export const CONTRASTE_MINIMO = 4.5;

/**
 * A partir de aqui el codigo se lee sin dramas en cualquier condicion. Entre
 * este valor y el minimo se guarda, pero avisando.
 */
export const CONTRASTE_COMODO = 7;

export type VeredictoContraste =
  | { readonly nivel: 'invalido' }
  | { readonly nivel: 'insuficiente'; readonly ratio: number }
  | { readonly nivel: 'justo'; readonly ratio: number }
  | { readonly nivel: 'bueno'; readonly ratio: number };

/**
 * Juzga un par de colores de codigo.
 *
 * Ojo con el orden de los argumentos: el contraste es simetrico, pero la
 * legibilidad no. Un codigo claro sobre fondo oscuro (invertido) tiene el
 * mismo ratio y sin embargo muchos lectores antiguos no lo cogen, porque dan
 * por hecho modulos oscuros. Se trata como insuficiente aunque el numero salga
 * alto: es un fallo que solo aparece en el telefono del cliente y nunca en el
 * nuestro.
 */
export function juzgarContraste(
  foreground: string,
  background: string,
): VeredictoContraste {
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) return { nivel: 'invalido' };

  const fg = parseHex(foreground);
  const bg = parseHex(background);
  if (fg && bg && relativeLuminance(fg) > relativeLuminance(bg)) {
    return { nivel: 'insuficiente', ratio };
  }

  if (ratio < CONTRASTE_MINIMO) return { nivel: 'insuficiente', ratio };
  if (ratio < CONTRASTE_COMODO) return { nivel: 'justo', ratio };
  return { nivel: 'bueno', ratio };
}
